import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, setAuthToken } from '../../apiClient';
import { Card, CardContent } from '../ui/card';
import { Loader, CheckCircle, XCircle, KeyRound, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Eye, EyeOff } from 'lucide-react';

interface AuthCallbackProps {
  onComplete?: () => void;
}

export default function AuthCallback({ onComplete }: AuthCallbackProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [authType, setAuthType] = useState<string | null>(null);
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Parse hash parameters
        const hashParams = new URLSearchParams(location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');
        
        console.log('Auth callback params:', { accessToken: accessToken?.substring(0, 20) + '...', type });

        if (!accessToken || !type) {
          setError('Invalid auth callback - missing required parameters');
          setIsProcessing(false);
          return;
        }

        setAuthType(type);

        if (type === 'recovery') {
          // For password recovery, we need the user to set a new password
          try {
            // Set the session first
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || ''
            });

            if (error || !data.user) {
              throw new Error(error?.message || 'Failed to establish session');
            }

            setEmail(data.user.email || '');
            setNeedsPasswordReset(true);
            setIsProcessing(false);
            
          } catch (err: any) {
            console.error('Recovery session error:', err);
            setError(err.message || 'Failed to process recovery link');
            setIsProcessing(false);
          }

        } else if (type === 'signup') {
          // Handle email confirmation for new signups
          try {
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || ''
            });

            if (error || !data.user) {
              throw new Error(error?.message || 'Failed to confirm email');
            }

            // Store auth token and user data
            setAuthToken(accessToken);
            
            // Decode JWT to get user information
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            
            // Store user metadata
            if (payload.sub) {
              localStorage.setItem('user_id', payload.sub);
            }
            if (payload.user_role) {
              localStorage.setItem('user_role', payload.user_role);
            }
            if (payload.organization_id) {
              localStorage.setItem('organization_id', payload.organization_id);
            }

            // Store user object in localStorage
            const user = {
              id: payload.sub || '',
              email: data.user.email || '',
              name: payload.name || '',
              role: (payload.user_role || 'holder'),
              tenantId: payload.organization_id || undefined,
              tenantName: payload.organization_name || undefined
            };
            localStorage.setItem('credential-capital-user', JSON.stringify(user));

            // Store tenant info if available
            if (payload.organization_id) {
              const minimalTenant = { 
                id: payload.organization_id, 
                name: '', 
                subdomain: '', 
                primaryColor: '', 
                status: 'active', 
                createdAt: '' 
              };
              localStorage.setItem('credential-capital-tenant', JSON.stringify(minimalTenant));
            } else {
              localStorage.removeItem('credential-capital-tenant');
            }

            setSuccess('Email confirmed successfully! Redirecting to dashboard...');
            
            setTimeout(() => {
              const roleRoute = payload.user_role === 'issuer' ? 'issuer' : 
                             payload.user_role === 'admin' ? 'admin' :
                             payload.user_role === 'verifier' ? 'verifier' : 'recipient';
              navigate(`/${roleRoute}`);
              onComplete?.();
            }, 2000);

          } catch (err: any) {
            console.error('Email confirmation error:', err);
            setError(err.message || 'Failed to confirm email');
            setIsProcessing(false);
          }

        } else {
          setError(`Unknown auth callback type: ${type}`);
          setIsProcessing(false);
        }

      } catch (err: any) {
        console.error('Auth callback error:', err);
        setError(err.message || 'Failed to process auth callback');
        setIsProcessing(false);
      }
    };

    if (location.hash) {
      handleAuthCallback();
    } else {
      setError('No auth parameters found in URL');
      setIsProcessing(false);
    }
  }, [location.hash, navigate, onComplete]);

  const validatePassword = (password: string) => {
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (!/(?=.*[a-z])/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/(?=.*\d)/.test(password)) return 'Password must contain at least one number';
    return null;
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Update user's password
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        throw error;
      }

      setSuccess('Password reset successfully! Redirecting...');
      
      setTimeout(() => {
        navigate('/recipient'); // Default to recipient dashboard
        onComplete?.();
      }, 2000);

    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to reset password');
    } finally {
      setIsProcessing(false);
    }
  };

  // Loading state
  if (isProcessing && !needsPasswordReset) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cool-gray-100 to-blue-50 flex items-center justify-center px-4">
        <Card className="credential-capital-card w-full max-w-md bg-white">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <Loader className="h-8 w-8 animate-spin text-electric-steel" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-charcoal-ink">
                  Processing Authentication
                </h3>
                <p className="text-sm text-warm-slate">
                  Please wait while we verify your authentication...
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Password reset form
  if (needsPasswordReset) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cool-gray-100 to-blue-50 py-8 px-4">
        <div className="w-full max-w-md mx-auto space-y-6">
          {/* Header */}
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="bg-midnight-indigo p-3 rounded-2xl credential-capital-shadow-lg">
                <KeyRound className="h-10 w-10 text-white" />
              </div>
            </div>
            <h1 className="font-display text-3xl font-bold text-midnight-indigo mb-2">
              Set New Password
            </h1>
            <p className="text-warm-slate">
              Set a new password for your account
            </p>
          </div>

          {/* Form */}
          <Card className="credential-capital-card bg-white">
            <CardContent className="pt-6 space-y-4">
              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {error}
                  </AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-verified-green" />
                  <AlertDescription className="text-green-800">
                    {success}
                  </AlertDescription>
                </Alert>
              )}

              {email && (
                <p className="text-sm text-warm-slate text-center">
                  Resetting password for: <span className="font-semibold">{email}</span>
                </p>
              )}

              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your new password"
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-warm-slate hover:text-charcoal-ink"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your new password"
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-warm-slate hover:text-charcoal-ink"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Password Requirements */}
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h4 className="text-sm font-semibold text-charcoal-ink mb-2">Password Requirements:</h4>
                  <ul className="text-xs text-warm-slate space-y-1">
                    <li className={`flex items-center ${password.length >= 8 ? 'text-verified-green' : ''}`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-2 ${password.length >= 8 ? 'bg-verified-green' : 'bg-gray-300'}`} />
                      At least 8 characters
                    </li>
                    <li className={`flex items-center ${/(?=.*[a-z])/.test(password) ? 'text-verified-green' : ''}`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-2 ${/(?=.*[a-z])/.test(password) ? 'bg-verified-green' : 'bg-gray-300'}`} />
                      One lowercase letter
                    </li>
                    <li className={`flex items-center ${/(?=.*[A-Z])/.test(password) ? 'text-verified-green' : ''}`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-2 ${/(?=.*[A-Z])/.test(password) ? 'bg-verified-green' : 'bg-gray-300'}`} />
                      One uppercase letter
                    </li>
                    <li className={`flex items-center ${/(?=.*\d)/.test(password) ? 'text-verified-green' : ''}`}>
                      <div className={`w-1.5 h-1.5 rounded-full mr-2 ${/(?=.*\d)/.test(password) ? 'bg-verified-green' : 'bg-gray-300'}`} />
                      One number
                    </li>
                  </ul>
                </div>

                <Button 
                  type="submit" 
                  className="w-full credential-capital-button-primary"
                  disabled={isProcessing || !password || !confirmPassword}
                >
                  {isProcessing ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Resetting Password...
                    </div>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Error or success state
  return (
    <div className="min-h-screen bg-gradient-to-br from-cool-gray-100 to-blue-50 flex items-center justify-center px-4">
      <Card className="credential-capital-card w-full max-w-md bg-white">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            {error ? (
              <>
                <XCircle className="h-12 w-12 text-red-500 mx-auto" />
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-ink">Authentication Error</h3>
                  <p className="text-sm text-warm-slate mt-2">{error}</p>
                </div>
                <Button onClick={() => navigate('/login')} variant="outline">
                  Back to Login
                </Button>
              </>
            ) : (
              <>
                <CheckCircle className="h-12 w-12 text-verified-green mx-auto" />
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-ink">Success!</h3>
                  <p className="text-sm text-warm-slate mt-2">{success}</p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}